import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import UserEditor from './UserEditor';
import UserSessionsModal from './UserSessionsModal';
import { chip, chipActive, chipIdle } from '@/components/ui/design';
import { fetchAdminUsers, type AdminUser } from '@/api/adminApi';
import { startImpersonating } from './impersonation';
import { EmptyRow, Pager, SearchBox, fullName, formatDate, pill, rowBorder, smallBtn, tableWrap, td, th, useDebounced } from './shared';

const LIMIT = 20;

export default function UsersTab() {
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [role, setRole] = useState<'' | 'admin' | 'member'>('');
    const [editing, setEditing] = useState<AdminUser | null>(null);
    const [sessionsFor, setSessionsFor] = useState<AdminUser | null>(null);
    const debounced = useDebounced(search.trim());
    const { data, isLoading, isError } = useQuery({
        queryKey: ['admin-users', page, debounced, role],
        queryFn: () => fetchAdminUsers({ page, limit: LIMIT, search: debounced || undefined, role: role || undefined }),
        placeholderData: previous => previous,
    });

    const impersonate = (user: AdminUser) => {
        if (!user.email) return;
        startImpersonating(user.email);
        window.location.assign('/');
    };

    return (
        <div>
            <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center">
                <SearchBox value={search} onChange={value => { setSearch(value); setPage(1); }} placeholder="Search users by name or email" />
                <div className="flex flex-wrap gap-2" role="tablist" aria-label="User filter">
                    {([['', 'All'], ['admin', 'Admins'], ['member', 'Organization members']] as const).map(([value, text]) => (
                        <button key={text} role="tab" aria-selected={role === value} onClick={() => { setRole(value); setPage(1); }} className={`${chip} ${role === value ? chipActive : chipIdle}`}>{text}</button>
                    ))}
                </div>
            </div>
            {isError && <p className="mb-3 text-sm text-red-500">Failed to load users.</p>}
            <div className={tableWrap}>
                <table className="w-full min-w-[760px]">
                    <thead>
                        <tr><th className={th}>User</th><th className={th}>Organizations</th><th className={th}>Joined</th><th className={th} /></tr>
                    </thead>
                    <tbody>
                        {(data?.data ?? []).map(user => (
                            <tr key={user._id} className={rowBorder}>
                                <td className={td}>
                                    <div className="font-semibold">{fullName(user) || user.username}</div>
                                    <div className="text-grey40 dark:text-nightMuted">{user.email}</div>
                                    {user.isAdmin && <span className={`${pill} mt-1`}>Admin</span>}
                                </td>
                                <td className={td}>
                                    <div className="flex flex-wrap gap-1.5">
                                        {user.organizations.length === 0 && <span className="text-grey40 dark:text-nightMuted">None</span>}
                                        {user.organizations.map(org => <span key={`${org.organizerId}-${org.role}`} className={pill}>{org.orgName} · {org.role}</span>)}
                                    </div>
                                </td>
                                <td className={td}>{formatDate(user.createdAt)}</td>
                                <td className={`${td} space-x-2 text-right whitespace-nowrap`}>
                                    <button type="button" className={smallBtn} onClick={() => setEditing(user)}>Edit</button>
                                    <button type="button" className={smallBtn} disabled={!user.email} onClick={() => setSessionsFor(user)}>Sessions</button>
                                    <button type="button" className={smallBtn} disabled={!user.email} onClick={() => impersonate(user)}>
                                        Impersonate
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {(data?.data ?? []).length === 0 && <EmptyRow colSpan={4} loading={isLoading} text="No users found." />}
                    </tbody>
                </table>
            </div>
            <Pager page={page} limit={LIMIT} total={data?.total ?? 0} onPage={setPage} />
            {editing && <UserEditor user={editing} onClose={() => setEditing(null)} />}
            {sessionsFor && <UserSessionsModal email={sessionsFor.email ?? ''} onClose={() => setSessionsFor(null)} />}
        </div>
    );
}
